import { collection, getDocs, limit, query, startAfter, where } from "firebase/firestore";

// Fetch on demand only. The email filter is applied on the server for every page.
export async function fetchEditLogs({ db, email, isCurrent = () => true, onProgress = () => {}, pageSize = 250 }) {
  if (!email || !isCurrent()) throw new Error("edit-logs-account-changed");
  const rows = [];
  let cursor = null;
  while (true) {
    if (!isCurrent()) throw new Error("edit-logs-account-changed");
    const constraints = [where("email", "==", email), limit(pageSize)];
    if (cursor) constraints.push(startAfter(cursor));
    // No timestamp ordering: old documents without timestamps must also be included.
    const snapshot = await getDocs(query(collection(db, "boundingBoxLogs"), ...constraints));
    if (!isCurrent()) throw new Error("edit-logs-account-changed");
    for (const document of snapshot.docs) {
      const data = document.data();
      if (data.email !== email) throw new Error("edit-logs-owner-mismatch");
      rows.push({ ...data, id: document.id });
    }
    onProgress(rows.length);
    if (snapshot.docs.length < pageSize) break;
    cursor = snapshot.docs[snapshot.docs.length - 1];
  }
  return rows.sort((a, b) => logTimestampMillis(b.timestamp) - logTimestampMillis(a.timestamp) || String(a.id).localeCompare(String(b.id)));
}

function logTimestampMillis(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (typeof value === "string") return Date.parse(value) || 0;
  return 0;
}

function csvCell(value) {
  let text = value == null ? "" : String(value);
  // Keep spreadsheet applications from interpreting user-entered text as formulas.
  if (/^[\s]*[=+\-@]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}

export function buildEditLogsCsv(logs) {
  const headers = ["id", "timestamp", "email", "photopath", "cultiverName", "boxId", "action", "details_json", "userAgent"];
  const rows = [headers];
  for (const log of logs) {
    const ms = logTimestampMillis(log.timestamp);
    const date = ms ? new Date(ms) : null;
    const timestamp = date && Number.isFinite(date.getTime()) ? date.toISOString() : "";
    const details = JSON.stringify(log.details ?? {}, (key, value) =>
      typeof value?.toDate === "function" ? value.toDate().toISOString() : value);
    rows.push([log.id, timestamp, log.email, log.photopath, log.cultiverName, log.boxId, log.action, details, log.userAgent]);
  }
  return "\uFEFF" + rows.map(row => row.map(csvCell).join(",")).join("\r\n");
}
